import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Lock, X } from "lucide-react";
import { Logo } from "../Logo";
import { api } from "../api";
import { useAuth } from "../auth";

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <main className="login v2" id="main-content">
      <div className="loginleft">
        <Logo />
        <div className="hero">
          <span className="eyebrow-dark">LARUS</span>
          <h1>The work moving forward. The decisions that need you.</h1>
          <p>The private programme workspace for AI data-centre deployment: sources, capacity, capital and handover in one exact record.</p>
        </div>
        <div className="loginfoot"><Lock size={13} /> Invitation only · larus.canus.io</div>
      </div>
      <div className="loginright"><div className="loginbox">{children}</div></div>
    </main>
  );
}

function RequestAccess({ close }: { close: () => void }) {
  const [f, setF] = useState({ name: "", email: "", organisation: "", note: "", website: "" });
  const [state, setState] = useState<"idle" | "busy" | "done">("idle");
  const [err, setErr] = useState("");
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", h);
    return () => { window.removeEventListener("keydown", h); };
  }, [close]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setState("busy"); setErr("");
    try { await api("/auth/request-access", { body: f }); setState("done"); } catch (x: any) { setErr(x.message); setState("idle"); }
  };
  return createPortal(
    <div className="dialog-scrim" onClick={close}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="ra-title" onClick={(e) => e.stopPropagation()} data-testid="request-demo-modal">
        <button className="ghost dialog-x" onClick={close} aria-label="Close" data-testid="request-demo-close-button"><X size={16} /></button>
        {state === "done" ? <div data-testid="request-demo-success-message"><h2 id="ra-title">Request received</h2>
          <p className="muted">Thank you. The CANUS team reviews every request and will contact you directly.</p>
          <button className="primary wide" onClick={close} data-testid="request-demo-done-button">Back to sign in</button></div> :
          <form onSubmit={submit}>
            <h2 id="ra-title">Request demo access</h2>
            <p className="muted tiny" style={{ margin: "6px 0 18px" }}>LARUS is available by invitation. Tell us who you are and we will be in touch.</p>
            <label htmlFor="ra-name">Full name</label><input id="ra-name" autoFocus required minLength={2} value={f.name} onChange={set("name")} data-testid="request-demo-name-input" />
            <label htmlFor="ra-email">Work email</label><input id="ra-email" type="email" required value={f.email} onChange={set("email")} data-testid="request-demo-email-input" />
            <label htmlFor="ra-org">Organisation</label><input id="ra-org" required minLength={2} value={f.organisation} onChange={set("organisation")} data-testid="request-demo-org-input" />
            <label htmlFor="ra-note">Programme or interest (optional)</label><textarea id="ra-note" rows={3} maxLength={1000} value={f.note} onChange={set("note")} data-testid="request-demo-note-input" />
            <input className="hp" tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={set("website")} />
            {err && <div className="notice red" role="alert">{err}</div>}
            <button className="primary wide" disabled={state === "busy"} data-testid="request-demo-submit-button">{state === "busy" ? "Sending…" : "Request access"}</button>
          </form>}
      </div>
    </div>,
    document.body
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [req, setReq] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await login(email, password); nav("/"); } catch (x: any) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <Frame>
      <h1>Sign in</h1><p className="muted tiny" style={{ marginBottom: 26 }}>Access is by invitation only.</p>
      <form onSubmit={submit} data-testid="login-form">
        <label htmlFor="email">Work email</label>
        <input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
        <div className="spread"><label htmlFor="pw">Password</label><Link to="/forgot-password" className="tiny link" data-testid="forgot-password-link">Forgot password?</Link></div>
        <input id="pw" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
        {err && <div className="notice red" role="alert" data-testid="login-error">{err}</div>}
        <button className="primary wide" disabled={busy} data-testid="login-submit-button">{busy ? "Signing in…" : "Sign in"}</button>
      </form>
      <div className="divider"><span>New to LARUS?</span></div>
      <button className="outline wide" onClick={() => setReq(true)} data-testid="request-demo-button">Request demo access <ArrowRight size={15} /></button>
      {req && <RequestAccess close={() => setReq(false)} />}
    </Frame>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  return (
    <Frame>
      <h1>Account recovery</h1>
      <form onSubmit={async (e) => { e.preventDefault(); const r = await api("/auth/forgot-password", { body: { email } }).catch((x) => ({ message: x.message })); setMsg(r.message); }}>
        <label className="tiny muted" htmlFor="fe">Work email</label>
        <input id="fe" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
        <button className="primary" data-testid="forgot-submit-button">Send recovery link</button>
      </form>
      {msg && <div className="notice neutral" data-testid="forgot-result">{msg}</div>}
      <Link to="/login" className="tiny muted">Back to sign in</Link>
    </Frame>
  );
}

function TokenForm({ kind }: { kind: "reset" | "invite" }) {
  const [sp] = useSearchParams();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr("");
    try {
      if (kind === "reset") { await api("/auth/reset-password", { body: { token: sp.get("token"), password: pw } }); setDone(true); }
      else { setUser(await api("/auth/accept-invite", { body: { token: sp.get("token"), name, password: pw } })); nav("/"); }
    } catch (x: any) { setErr(x.message); }
  };
  return (
    <Frame>
      <h1>{kind === "reset" ? "Choose a new password" : "Accept invitation"}</h1>
      {done ? <div className="notice neutral" data-testid="reset-done">Password changed. All existing sessions were signed out. <Link to="/login">Sign in</Link></div> :
        <form onSubmit={submit}>
          {kind === "invite" && <><label className="tiny muted" htmlFor="nm">Your name</label><input id="nm" required value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name-input" /></>}
          <label className="tiny muted" htmlFor="np">New password (10+ characters)</label>
          <input id="np" type="password" minLength={10} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="token-password-input" />
          {err && <div className="notice red" role="alert" data-testid="token-error">{err}</div>}
          <button className="primary" data-testid="token-submit-button">Continue</button>
        </form>}
    </Frame>
  );
}

export const Reset = () => <TokenForm kind="reset" />;
export const AcceptInvite = () => <TokenForm kind="invite" />;
